import React, { useEffect, useState } from 'react';
import axios from 'axios';
import IntranetLayout from './IntranetLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { student_id: '', date: '', robot_id: '', activities: [], commentaire: '' };

export const IntranetSeances = () => {
  const [seances, setSeances] = useState([]);
  const [students, setStudents] = useState([]);
  const [robots, setRobots] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedProgramme, setSelectedProgramme] = useState('');

  const fetchAll = async () => {
    const [sRes, stRes, rRes] = await Promise.all([
      axios.get(`${API}/intranet/seances`),
      axios.get(`${API}/intranet/students`),
      axios.get(`${API}/intranet/robots`),
    ]);
    setSeances(sRes.data);
    setStudents(stRes.data);
    setRobots(rRes.data);
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load activities when student changes
  useEffect(() => {
    if (selectedProgramme) {
      axios.get(`${API}/intranet/activities?programme=${selectedProgramme}`).then(r => setActivities(r.data));
    }
  }, [selectedProgramme]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStudentChange = (studentId) => {
    setForm({ ...form, student_id: studentId });
    const student = students.find(s => s.id === studentId);
    if (student) setSelectedProgramme(student.programme);
  };

  const toggleActivity = (actId) => {
    setForm(prev => ({
      ...prev,
      activities: prev.activities.includes(actId) ? prev.activities.filter(a => a !== actId) : [...prev.activities, actId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/intranet/seances/${editingId}`, form);
        toast.success('Séance modifiée');
      } else {
        await axios.post(`${API}/intranet/seances`, form);
        toast.success('Séance enregistrée');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); setSelectedProgramme(''); fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleEdit = (s) => {
    setForm(s);
    setEditingId(s.id);
    const student = students.find(st => st.id === s.student_id);
    if (student) setSelectedProgramme(student.programme);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette séance ?')) return;
    await axios.delete(`${API}/intranet/seances/${id}`);
    toast.success('Supprimée'); fetchAll();
  };

  const getStudentName = (id) => { const s = students.find(s => s.id === id); return s ? `${s.prenom} ${s.nom}` : id; };
  const getRobotName = (id) => { const r = robots.find(r => r.id === id); return r ? r.name : '-'; };
  const getActivityTitles = (ids) => {
    if (!ids || ids.length === 0) return '-';
    return ids.map(id => {
      // search in all activities (could be from any programme)
      return activities.find(a => a.id === id)?.titre || id;
    }).join(', ');
  };

  return (
    <IntranetLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">Séances</h2>
            <p className="text-sm text-slate-500">{seances.length} séances enregistrées</p>
          </div>
          <Button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); setSelectedProgramme(''); }} className="btn-primary" data-testid="add-seance-btn">
            <Plus size={16} className="mr-1" /> Nouvelle séance
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-extrabold">{editingId ? 'Modifier' : 'Nouvelle'} séance</h3>
                <button onClick={() => setShowForm(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="seance-form">
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Élève *</Label>
                  <Select value={form.student_id} onValueChange={handleStudentChange}>
                    <SelectTrigger className="rounded-lg" data-testid="select-student"><SelectValue placeholder="Choisir un élève" /></SelectTrigger>
                    <SelectContent>
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} ({s.programme})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Date *</Label>
                  <Input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-lg" data-testid="input-date" />
                </div>
                <div>
                  <Label className="text-xs font-semibold mb-1 block">Robot utilisé</Label>
                  <Select value={form.robot_id} onValueChange={v => setForm({...form, robot_id: v})}>
                    <SelectTrigger className="rounded-lg" data-testid="select-robot"><SelectValue placeholder="Choisir un robot" /></SelectTrigger>
                    <SelectContent>
                      {robots.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Activities Checkboxes */}
                {activities.length > 0 && (
                  <div>
                    <Label className="text-xs font-semibold mb-2 block">Activités réalisées ({selectedProgramme})</Label>
                    <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3 space-y-1.5">
                      {activities.map(a => (
                        <label key={a.id} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-xs transition-colors ${form.activities.includes(a.id) ? 'bg-blue-50 text-[#1B6FE3]' : 'hover:bg-slate-50'}`}>
                          <input type="checkbox" checked={form.activities.includes(a.id)} onChange={() => toggleActivity(a.id)} className="mt-0.5 rounded" />
                          <span><strong>Activité {a.numero}:</strong> {a.titre}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs font-semibold mb-1 block">Commentaire</Label>
                  <Textarea value={form.commentaire} onChange={e => setForm({...form, commentaire: e.target.value})} rows={3} className="rounded-lg" data-testid="input-commentaire" />
                </div>
                <Button type="submit" className="w-full btn-primary justify-center" data-testid="submit-seance">{editingId ? 'Enregistrer' : 'Créer la séance'}</Button>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        <div className="space-y-3">
          {seances.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">Aucune séance enregistrée</div>
          ) : seances.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`seance-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-900">{getStudentName(s.student_id)}</span>
                    <span className="text-xs text-slate-500">{s.date}</span>
                  </div>
                  {s.robot_id && <p className="text-xs text-slate-600">Robot: <strong>{getRobotName(s.robot_id)}</strong></p>}
                  {s.activities && s.activities.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">Activités: {s.activities.length} réalisée(s)</p>
                  )}
                  {s.commentaire && <p className="text-xs text-slate-500 mt-1 italic">"{s.commentaire}"</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(s)} className="text-[#1B6FE3] hover:bg-blue-50 p-1.5 rounded-lg"><Pencil size={15} /></button>
                  <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </IntranetLayout>
  );
};

export default IntranetSeances;
